import { useCallback, useMemo } from 'react'
import { type F0Message, type UploadedFile } from '@factorialco/f0-react'
import { useAiChat } from 'modules/ai/chat/useAiChat'

import { useActivity } from 'modules/core/lib/activities/activity'
import type { ActivityComponent } from 'modules/core/lib/activities/types'
import i18n from 'modules/core/lib/i18n'
import { CreateTicketDialog } from 'modules/ticketing/components/CreateTicketDialog'

type Data = {
  title?: string
  description?: string
  categoryId?: string
  categoryName?: string
  priority?: string
}

// f0 emits uploaded files with `mimetype`; the chat runtime expects `binary`
// content parts with `mimeType`, mirroring ConnectedChatInput's encoding.
type UserTextPart = { type: 'text'; text: string }
type UserBinaryPart = {
  type: 'binary'
  url: string
  filename: string
  mimeType: string
}

/**
 * Builds the message handed to the chat runtime. Plain text when there are no
 * attachments; otherwise a multipart message with one `binary` part per file
 * (re-keying f0's `mimetype` to the runtime's `mimeType`) followed by the
 * prompt text — the same shape ConnectedChatInput produces. Assigns cleanly to
 * `F0Message` (whose `content` is `unknown`), so no type-assertion is needed.
 */
export const buildLaunchedChatMessage = (
  prompt: string,
  files: UploadedFile[],
  generateId: () => string
): string | F0Message => {
  if (files.length === 0) return prompt

  const content: Array<UserTextPart | UserBinaryPart> = [
    ...files.map((file) => ({
      type: 'binary' as const,
      url: file.url,
      filename: file.filename,
      mimeType: file.mimetype,
    })),
    { type: 'text' as const, text: prompt },
  ]

  return { id: generateId(), role: 'user', content }
}

export const CreateTicketActivity: ActivityComponent<Data> = ({
  title,
  description,
  categoryId,
  categoryName,
  priority,
}) => {
  const { open, closeActivity } = useActivity()
  const aiChat = useAiChat()
  const initialValues = useMemo(
    () => ({ title, description, categoryId, categoryName, priority }),
    [categoryId, categoryName, description, priority, title]
  )

  const handleLaunchAiTicketCreation = useCallback(
    (
      message: string,
      options?: { source?: 'description' | 'category'; files?: UploadedFile[] }
    ) => {
      const trimmedMessage = message.trim()
      const files = options?.files ?? []
      if (!trimmedMessage && files.length === 0) return

      const ticketingPrompt =
        options?.source === 'category'
          ? trimmedMessage
          : `${i18n.t('ticketing.inbox.create.ai_description_message_prefix')} ${trimmedMessage}`
      aiChat.clear()
      aiChat.setVisualizationMode('fullscreen')
      aiChat.setOpen(true)
      closeActivity()

      window.setTimeout(() => {
        aiChat.sendMessage(
          buildLaunchedChatMessage(ticketingPrompt, files, () => crypto.randomUUID())
        )
      }, 0)
    },
    [aiChat, closeActivity]
  )

  return (
    <CreateTicketDialog
      isOpen={open}
      onClose={closeActivity}
      initialValues={initialValues}
      fileAttachments={aiChat.fileAttachments}
      onLaunchAiTicketCreation={handleLaunchAiTicketCreation}
    />
  )
}
